import { useEffect, useMemo, useState } from "react";
import { Receipt, Download } from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s + (s.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function RecentBillPayments({ refreshKey }) {
  const [entries, setEntries] = useState([]);
  const [cat, setCat] = useState("all"); // all | personal | business
  const [period, setPeriod] = useState("all"); // all | "YYYY-M"

  useEffect(() => {
    api.get("/snapbudget/bill-ledger").then((r) => setEntries(r.data.entries || [])).catch(() => {});
  }, [refreshKey]);

  const periodOptions = useMemo(() => {
    const seen = new Map();
    for (const e of entries) {
      if (e.year && e.month) {
        const key = `${e.year}-${e.month}`;
        if (!seen.has(key)) seen.set(key, `${MONTHS[e.month - 1]} ${e.year}`);
      }
    }
    return Array.from(seen.entries());
  }, [entries]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (cat !== "all" && (e.category || "personal") !== cat) return false;
      if (period !== "all" && `${e.year}-${e.month}` !== period) return false;
      return true;
    });
  }, [entries, cat, period]);

  const visible = filtered.slice(0, 6);

  const exportCsv = () => {
    const rows = [["Vendor", "Date", "Type", "Category", "Amount"]];
    for (const e of filtered) {
      rows.push([e.title || "Bill", e.date || "", e.category || "personal",
                 e.snap_category || "", (Number(e.amount) || 0).toFixed(2)]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bill-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pill = (val, label, key) => (
    <button
      key={val}
      data-testid={`snap-recent-filter-${key}-${val}`}
      onClick={() => (key === "cat" ? setCat(val) : setPeriod(val))}
      className={`px-2.5 py-1 rounded-full text-[11px] transition-colors ${
        (key === "cat" ? cat : period) === val
          ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
          : "text-slate-400 border border-white/10 hover:text-slate-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section data-testid="snap-recent-bill-payments" className="bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-bold tracking-wide text-emerald-300 flex items-center gap-2">
          <Receipt className="w-5 h-5" /> Recent bill payments
        </h2>
        <button
          data-testid="snap-recent-export"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-white/15 text-slate-300 text-xs hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Download CSV"
        >
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {pill("all", "All", "cat")}
        {pill("personal", "Personal", "cat")}
        {pill("business", "Business", "cat")}
        {periodOptions.length > 0 && <span className="mx-1 h-4 w-px bg-white/10" />}
        {periodOptions.length > 0 && (
          <select
            data-testid="snap-recent-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-[#0a1226] border border-white/10 rounded-full text-[11px] text-slate-300 px-2.5 py-1 focus:outline-none focus:border-emerald-500/40"
          >
            <option value="all">All months</option>
            {periodOptions.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        )}
      </div>

      {visible.length === 0 ? (
        <p data-testid="snap-recent-empty" className="text-sm text-slate-500 py-6 text-center">
          {entries.length === 0
            ? "No bill payments logged yet. Mark a bill paid and it shows up here."
            : "No payments match these filters."}
        </p>
      ) : (
        <ul className="divide-y divide-white/5" data-testid="snap-recent-list">
          {visible.map((it, i) => (
            <li key={i} data-testid="snap-recent-item" className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-slate-100 truncate">{it.title || "Bill"}</p>
                <p className="text-[11px] text-slate-500">
                  {fmtDate(it.date)}
                  {it.category === "business" && <span className="ml-1.5 text-indigo-300/70">· Business</span>}
                </p>
              </div>
              <span className="font-mono text-sm text-rose-300 shrink-0">−{money(it.amount)}</span>
            </li>
          ))}
        </ul>
      )}

      {filtered.length > 6 && (
        <p className="mt-3 text-[11px] text-slate-500 text-center">
          Showing 6 of {filtered.length} — export CSV for the full history.
        </p>
      )}
    </section>
  );
}
